import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { brandApi, api, type CampaignAssetKind } from '../../lib/api';

const VERTICALS = [
  'music',
  'finance',
  'crypto',
  'legal',
  'real-estate',
  'tech',
  'health',
  'education',
  'fashion',
  'food',
  'sports',
  'gaming',
  'entertainment',
  'general',
];

const TOTAL_STEPS = 5;

// Upload buckets. Each maps to a `kind` accepted by POST /api/campaigns/:id/assets.
const UPLOAD_GROUPS: Array<{
  kind: CampaignAssetKind;
  label: string;
  helper: string;
  accept: string;
}> = [
  {
    kind: 'audio',
    label: 'Audio / sound',
    helper: 'The track or sound clips should use',
    accept: 'audio/*',
  },
  {
    kind: 'video',
    label: 'Reference videos',
    helper: 'Examples of the style or edit you want',
    accept: 'video/*',
  },
  {
    kind: 'image',
    label: 'Brief docs and images',
    helper: 'Mood boards, logos, brand assets, PDF briefs',
    accept: 'image/*,application/pdf',
  },
  {
    kind: 'source',
    label: 'Source footage',
    helper: 'Raw clips for creators to cut from',
    accept: 'video/*',
  },
];

type UploadState =
  | { status: 'uploading'; progress: number }
  | { status: 'done'; assetId: string }
  | { status: 'error'; message: string };

interface UploadRow {
  id: string;
  name: string;
  size: number;
  kind: CampaignAssetKind;
  state: UploadState;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function Brief() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    title: '',
    source_video_url: '',
    vertical: 'general',
    clips_wanted: '',
    track: '',
    budget_total_dollars: '',
    cpm_rate: '15',
    target_verified_views: '',
    starts_at: '',
    ends_at: '',
    brief: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The campaign is created when the brand reaches the uploads step (step 4),
  // because assets attach to a campaign id. We hold the created id so uploads
  // and the final submit reuse the same record instead of creating duplicates.
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const budget = Number(form.budget_total_dollars) || 0;
  const cpm = Number(form.cpm_rate) || 0;
  const clipsWanted = form.clips_wanted ? Number(form.clips_wanted) : 0;
  const autoTarget = cpm > 0 ? Math.floor((budget / cpm) * 1000) : 0;
  const targetViews = form.target_verified_views ? Number(form.target_verified_views) : autoTarget;

  // Create the draft campaign. Idempotent within this form: if it already
  // exists (campaignId set) it is a no-op so we never double-create.
  async function ensureCampaign(): Promise<string | null> {
    if (campaignId) return campaignId;
    setCreating(true);
    setError(null);
    try {
      const created = await brandApi.brief({
        title: form.title,
        source_video_url: form.source_video_url,
        vertical: form.vertical,
        clips_wanted: clipsWanted || null,
        track: form.track || null,
        budget_total_dollars: budget,
        cpm_rate: cpm,
        target_verified_views: targetViews,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        brief: form.brief,
      });
      setCampaignId(created.id);
      return created.id;
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'detail' in e
          ? String((e as { detail: unknown }).detail)
          : 'Something went wrong creating the campaign.';
      setError(detail);
      return null;
    } finally {
      setCreating(false);
    }
  }

  async function next() {
    setError(null);
    // Entering the uploads step (4) creates the campaign first so files have
    // an id to attach to. If creation fails we stay on the current step.
    if (step === 3) {
      const id = await ensureCampaign();
      if (!id) return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }
  function back() {
    setStep((s) => Math.max(1, s - 1));
  }

  async function uploadFiles(kind: CampaignAssetKind, files: FileList | null) {
    if (!files || files.length === 0) return;
    const id = campaignId ?? (await ensureCampaign());
    if (!id) return;

    for (const file of Array.from(files)) {
      const rowId = `${kind}-${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((u) => [
        ...u,
        { id: rowId, name: file.name, size: file.size, kind, state: { status: 'uploading', progress: 0 } },
      ]);
      try {
        const res = await api.campaigns.uploadAsset(id, file, kind);
        setUploads((u) =>
          u.map((row) =>
            row.id === rowId
              ? { ...row, state: { status: 'done', assetId: res.asset.id } }
              : row
          )
        );
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Upload failed';
        setUploads((u) =>
          u.map((row) =>
            row.id === rowId ? { ...row, state: { status: 'error', message } } : row
          )
        );
      }
    }
  }

  const anyUploading = uploads.some((u) => u.state.status === 'uploading');

  async function submit() {
    setSubmitting(true);
    setError(null);
    // The campaign was already created on entry to the uploads step. Guard
    // against the edge case where someone reaches submit without it (e.g.
    // create failed silently) by ensuring it exists before navigating.
    const id = campaignId ?? (await ensureCampaign());
    if (!id) {
      setSubmitting(false);
      return;
    }
    navigate(`/app/campaigns/${id}?submitted=1`);
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <header style={{ marginBottom: 32 }}>
        <span
          style={{
            fontSize: 11,
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            color: tokens.color.ink[500],
          }}
        >
          Step {step} of {TOTAL_STEPS}
        </span>
        <h1
          style={{
            fontFamily: tokens.font.display,
            fontSize: 40,
            fontWeight: 400,
            fontStyle: 'normal',
            marginTop: 8,
          }}
        >
          Brief a campaign
        </h1>
      </header>

      <div
        style={{
          background: tokens.color.paper['00'],
          border: `1px solid ${tokens.color.line[100]}`,
          borderRadius: 12,
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {step === 1 && (
          <>
            <Input
              label="Campaign title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <Input
              label="Source video URL"
              required
              helper="The content to be clipped"
              value={form.source_video_url}
              onChange={(e) => setForm({ ...form, source_video_url: e.target.value })}
            />
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Vertical
              </label>
              <select
                value={form.vertical}
                onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                style={{
                  height: 40,
                  padding: '0 14px',
                  fontSize: 15,
                  background: tokens.color.paper['00'],
                  border: `1px solid ${tokens.color.line[100]}`,
                  borderRadius: 8,
                  width: '100%',
                }}
              >
                {VERTICALS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Input
                label="Number of clips wanted"
                type="number"
                min={1}
                helper="How many clips you want from the network"
                value={form.clips_wanted}
                onChange={(e) => setForm({ ...form, clips_wanted: e.target.value })}
              />
              <Input
                label="Sound / track"
                helper="Track or sound creators should use"
                value={form.track}
                onChange={(e) => setForm({ ...form, track: e.target.value })}
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <Input
              label="Total budget (USD)"
              type="number"
              required
              min={100}
              value={form.budget_total_dollars}
              onChange={(e) => setForm({ ...form, budget_total_dollars: e.target.value })}
            />
            <Input
              label="CPM rate ($ per 1K views)"
              type="number"
              required
              step="0.01"
              value={form.cpm_rate}
              onChange={(e) => setForm({ ...form, cpm_rate: e.target.value })}
            />
            <Input
              label="Target verified views"
              type="number"
              helper={`Auto-calculated: ${autoTarget.toLocaleString()}`}
              value={form.target_verified_views}
              onChange={(e) => setForm({ ...form, target_verified_views: e.target.value })}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Input
                label="Start date"
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              />
              <Input
                label="End date"
                type="date"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Notes for the creator network</label>
            <textarea
              value={form.brief}
              onChange={(e) => setForm({ ...form, brief: e.target.value })}
              rows={10}
              placeholder="Tone, do's, don'ts, target audience..."
              style={{
                padding: 16,
                fontSize: 15,
                background: tokens.color.paper['00'],
                border: `1px solid ${tokens.color.line[100]}`,
                borderRadius: 8,
                fontFamily: tokens.font.body,
                lineHeight: 1.55,
              }}
            />
            <p style={{ fontSize: 13, color: tokens.color.ink[500], lineHeight: 1.55 }}>
              Next you can attach files. We will save your campaign as a draft so
              uploads have somewhere to live.
            </p>
            {error && <ErrorBox message={error} />}
          </>
        )}

        {step === 4 && (
          <>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Attach files</h2>
              <p style={{ fontSize: 13, color: tokens.color.ink[500], lineHeight: 1.55, marginTop: 6 }}>
                Optional. Add anything that helps creators make the clips you want.
                You can add more later from the campaign page.
              </p>
            </div>

            {UPLOAD_GROUPS.map((group) => {
              const rows = uploads.filter((u) => u.kind === group.kind);
              return (
                <div
                  key={group.kind}
                  style={{
                    border: `1px solid ${tokens.color.line[100]}`,
                    borderRadius: 10,
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{group.label}</div>
                      <div style={{ fontSize: 12, color: tokens.color.ink[500], marginTop: 2 }}>
                        {group.helper}
                      </div>
                    </div>
                    <input
                      ref={(el) => {
                        fileInputs.current[group.kind] = el;
                      }}
                      type="file"
                      multiple
                      accept={group.accept}
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        void uploadFiles(group.kind, e.target.files);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={creating}
                      onClick={() => fileInputs.current[group.kind]?.click()}
                    >
                      Add files
                    </Button>
                  </div>

                  {rows.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rows.map((row) => (
                        <UploadRowView key={row.id} row={row} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {error && <ErrorBox message={error} />}
          </>
        )}

        {step === 5 && (
          <>
            <Summary label="Title" value={form.title} />
            <Summary label="Source video" value={form.source_video_url} mono />
            <Summary label="Vertical" value={form.vertical} />
            {clipsWanted > 0 && <Summary label="Clips wanted" value={String(clipsWanted)} />}
            {form.track && <Summary label="Sound / track" value={form.track} />}
            <Summary label="CPM rate" value={`$${cpm.toFixed(2)} per 1K`} />
            <Summary label="Target views" value={targetViews.toLocaleString()} />
            <Summary
              label="Files attached"
              value={String(uploads.filter((u) => u.state.status === 'done').length)}
            />
            <div style={{ height: 1, background: tokens.color.line[100] }} />
            <Summary label="Total budget" value={`$${budget.toLocaleString()}`} large />
            <p style={{ fontSize: 13, color: tokens.color.ink[500], lineHeight: 1.55 }}>
              Our team will review your brief and confirm pricing and timing within one business
              day. Nothing is charged at submission.
            </p>
            {error && <ErrorBox message={error} />}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
          <Button variant="ghost" disabled={step === 1 || creating} onClick={back}>
            Back
          </Button>
          {step < TOTAL_STEPS ? (
            <Button loading={creating && step === 3} onClick={next}>
              Continue
            </Button>
          ) : (
            <Button loading={submitting} disabled={anyUploading} onClick={submit}>
              Submit campaign
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadRowView({ row }: { row: UploadRow }) {
  let statusNode;
  if (row.state.status === 'uploading') {
    statusNode = (
      <span style={{ fontSize: 12, color: tokens.color.ink[500], fontFamily: tokens.font.mono }}>
        Uploading
      </span>
    );
  } else if (row.state.status === 'done') {
    statusNode = (
      <span style={{ fontSize: 12, color: tokens.color.accent.sage, fontFamily: tokens.font.mono }}>
        Uploaded
      </span>
    );
  } else {
    statusNode = (
      <span style={{ fontSize: 12, color: tokens.color.accent.rust }}>{row.state.message}</span>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        background: tokens.color.paper[50],
        borderRadius: 8,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            color: tokens.color.ink[900],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.name}
        </div>
        <div style={{ fontSize: 11, color: tokens.color.ink[500], fontFamily: tokens.font.mono }}>
          {formatBytes(row.size)}
        </div>
      </div>
      {statusNode}
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 12,
        background: 'rgba(212, 58, 47, 0.10)',
        color: tokens.color.accent.rust,
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}

function Summary({
  label,
  value,
  large,
  mono,
}: {
  label: string;
  value: string;
  large?: boolean;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 16,
      }}
    >
      <span style={{ fontSize: 13, color: tokens.color.ink[500] }}>{label}</span>
      <span
        style={{
          fontFamily: mono ? tokens.font.mono : tokens.font.body,
          fontSize: large ? 24 : 15,
          color: tokens.color.ink[900],
          fontWeight: large ? 500 : 400,
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}
